import Switch from '@/components/Switch/Switch';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import styled from '@emotion/styled';
import { produce } from 'immer';
import debounce from 'lodash/debounce';
import isNumber from 'lodash/isNumber';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { Divider, OpaqueTitlePart, StyledSlider, SubTitle, Title, TitleContainer } from '../common';
import { New } from '../common/banners/new';

const DEFAULT_MAX_RESPONSE_LENGTH = 150;
const MIN_RESPONSE_LENGTH = 50;
const MAX_RESPONSE_LENGTH = 400;
const UPDATE_STORE_DEBOUNCE_TIMEOUT_MS = 400;

const Container = styled.div`
    display: flex;
    flex-direction: column;

    width: 100%;
    padding: 12px;

    border: 1px solid ${theme.colors.grey.border};
    border-radius: 8px;
`;

const TopPartContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const StyledTitle = styled(Title)`
    font-size: 14px;
    line-height: 1;
`;

const StyledOpaqueTitlePart = styled(OpaqueTitlePart)`
    font-size: 14px;
`;

const StyledSubTitle = styled(SubTitle)`
    font-size: 12px;
`;

const StyledDivider = styled(Divider)`
    margin-top: 16px;

    color: ${theme.colors.grey.border};
`;

const BottomPartContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    gap: 16px;

    padding: 12px;
    margin-top: 12px;
`;

const SliderIndicator = styled.text`
    color: ${theme.colors.black[500]};
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.28px;
`;

export const MaxResponseLength = observer(() => {
    const agentEditorStore = useAgentEditorStore();

    const [enabled, setEnabled] = useState(isNumber(agentEditorStore.llm.prompt_customization?.max_response_length));
    const [maxResponseLength, setMaxResponseLength] = useState(
        agentEditorStore.llm.prompt_customization?.max_response_length ?? DEFAULT_MAX_RESPONSE_LENGTH
    );

    const updateStoreValues = useCallback(
        ({ maxResponseLength, enabled }: { maxResponseLength: number; enabled: boolean }) =>
            agentEditorStore.setLLM(
                produce(toJS(agentEditorStore.llm), llm => {
                    if (!enabled) {
                        delete llm.prompt_customization?.max_response_length;
                        return;
                    }

                    (llm.prompt_customization ??= {}).max_response_length = maxResponseLength;
                })
            ),
        []
    );

    const debouncedUpdateStoreValues = useCallback(debounce(updateStoreValues, UPDATE_STORE_DEBOUNCE_TIMEOUT_MS), []);

    const onSwitchChange = () => {
        setEnabled(enabled => {
            updateStoreValues({
                maxResponseLength,
                enabled: !enabled,
            });
            return !enabled;
        });
    };

    const onSliderChange = (_e: Event, value: number | number[]) => {
        setMaxResponseLength(value as number);
        debouncedUpdateStoreValues({
            maxResponseLength: value as number,
            enabled,
        });
    };

    return (
        <Container>
            <TopPartContainer>
                <TitleContainer>
                    <StyledTitle>
                        Max response length <StyledOpaqueTitlePart>(Optional)</StyledOpaqueTitlePart> <New />
                    </StyledTitle>
                    <StyledSubTitle>Specify the maximum amount of words in the response</StyledSubTitle>
                </TitleContainer>
                <Switch checked={enabled} onChange={onSwitchChange} />
            </TopPartContainer>
            {enabled && (
                <>
                    <StyledDivider />
                    <BottomPartContainer>
                        <SliderIndicator>{MIN_RESPONSE_LENGTH}</SliderIndicator>
                        <StyledSlider
                            disabled={!enabled}
                            min={MIN_RESPONSE_LENGTH}
                            max={MAX_RESPONSE_LENGTH}
                            step={1}
                            value={maxResponseLength}
                            onChange={onSliderChange}
                            valueLabelDisplay="auto"
                        />
                        <SliderIndicator>{MAX_RESPONSE_LENGTH}</SliderIndicator>
                    </BottomPartContainer>
                </>
            )}
        </Container>
    );
});
